import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Loader2, ArrowLeft, Send, MessageSquare, Phone, FileText, Bot } from "lucide-react";
import EntityHero from "../components/entity-detail/EntityHero";
import EntityKpiBar from "../components/entity-detail/EntityKpiBar";
import PartnerBadge from "../components/affiliate/PartnerBadge";
import AIProfileCard from "../components/entity-detail/AIProfileCard";
import EntityActivityTimeline from "../components/entity-detail/EntityActivityTimeline";

const STAGE_FALLBACK = {
  prospect: { label: "Prospect", color: "#64748b" },
  contact: { label: "Prise de contact", color: "#3b82f6" },
  qualification: { label: "Qualification", color: "#8b5cf6" },
  devis: { label: "Devis envoyé", color: "#f59e0b" },
  negociation: { label: "Négociation", color: "#f97316" },
  gagne: { label: "Gagné", color: "#22c55e" },
  perdu: { label: "Perdu", color: "#ef4444" },
};

const QUICK_STAGES = ["prospect", "contact", "qualification", "devis", "negociation", "gagne", "perdu"];

export default function OpportunityDetailPage() {
  const { oppId } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const { societeApi } = useSociete();
  const api = societeApi();

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [noteContent, setNoteContent] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [error, setError] = useState(null);
  const [stages, setStages] = useState(STAGE_FALLBACK);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [oRes, cfgRes] = await Promise.all([
        api.get(`/crm/opportunities/${oppId}/overview`),
        api.get("/crm/pipeline-config").catch(() => ({ data: null })),
      ]);
      setOverview(oRes.data);
      if (cfgRes.data?.stages) {
        const map = {};
        cfgRes.data.stages.forEach((s) => {
          map[s.key] = { label: s.label, color: s.color };
        });
        setStages(map);
      }
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Impossible de charger l'opportunité");
    } finally {
      setLoading(false);
    }
  }, [oppId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const r = await api.post(`/crm/opportunities/${oppId}/ai-evaluate`);
      setOverview((prev) => (prev ? { ...prev, ai_evaluation: r.data } : prev));
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Échec de l'évaluation IA");
    } finally {
      setEvaluating(false);
    }
  };

  const changeStage = async (newStage) => {
    try {
      await api.put(`/crm/opportunities/${oppId}`, { stage: newStage });
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur changement de stage");
    }
  };

  const addNote = async () => {
    if (!noteContent.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/crm/opportunities/${oppId}/notes`, { content: noteContent });
      setNoteContent("");
      reload();
    } finally {
      setSavingNote(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="opp-detail-loading">
        <div className="flex items-center gap-3 text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" /> Chargement de l'opportunité…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto" data-testid="opp-detail-error">
        <Link to="/admin/crm" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour au CRM
        </Link>
        <div className="bg-rose-500/10 text-rose-300 border border-rose-500/30 rounded-xl p-4 text-sm">{error}</div>
      </div>
    );
  }

  if (!overview) return null;

  const o = overview.opportunity;
  const stageCfg = stages[o.stage] || STAGE_FALLBACK[o.stage] || { label: o.stage || "—", color: "#64748b" };
  const calls = overview.calls || [];
  const invoices = overview.invoices || [];
  const timeline = overview.timeline || [];

  const heroEntity = {
    ...o,
    first_name: o.contact_name?.split(" ")[0] || "",
    last_name: o.contact_name?.split(" ").slice(1).join(" ") || "",
  };

  const heroActions = (
    <>
      {QUICK_STAGES.filter((st) => stages[st] || STAGE_FALLBACK[st]).map((st) => (
        <button
          key={st}
          onClick={() => changeStage(st)}
          disabled={o.stage === st}
          className={`text-[11px] px-2.5 py-1 rounded-lg transition-colors ${o.stage === st ? "bg-white/15 text-white" : "bg-white/5 text-slate-400 hover:text-white border border-white/5 hover:border-white/15"}`}
          data-testid={`hero-stage-${st}`}
        >
          {(stages[st] || STAGE_FALLBACK[st])?.label}
        </button>
      ))}
      <button
        onClick={evaluate}
        disabled={evaluating}
        className="text-[11px] px-2.5 py-1 rounded-lg bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25 border border-[#f7c935]/30 inline-flex items-center gap-1 disabled:opacity-50"
        data-testid="hero-evaluate-btn"
      >
        {evaluating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
        Évaluer IA
      </button>
    </>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="opp-detail-page">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/crm"
          className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          data-testid="back-to-crm-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour au pipeline
        </Link>
      </div>

      <EntityHero
        entity={heroEntity}
        kind="opportunity"
        statusBadge={{ label: stageCfg.label, color: stageCfg.color }}
        aiEvaluation={overview.ai_evaluation}
        actions={heroActions}
      />

      <EntityKpiBar kpis={overview.kpis} kind="opportunity" />

      {/* Iter 233 — Attribution partenaire (affiliation long terme) */}
      {o?.partner_attribution?.partner_id && (
        <PartnerBadge attribution={o.partner_attribution} />
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-[#0b0811] border border-white/5 w-full justify-start flex-wrap h-auto gap-0.5 p-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#3b82f6] data-[state=active]:text-white text-xs">
            Aperçu
          </TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] text-xs" data-testid="tab-ai">
            <Bot className="w-3 h-3 mr-1" /> Profil IA
          </TabsTrigger>
          <TabsTrigger value="timeline" className="data-[state=active]:bg-[#8b5cf6] data-[state=active]:text-white text-xs" data-testid="tab-timeline">
            Timeline ({timeline.length})
          </TabsTrigger>
          <TabsTrigger value="calls" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-xs" data-testid="tab-calls">
            <Phone className="w-3 h-3 mr-1" /> Appels ({calls.length})
          </TabsTrigger>
          <TabsTrigger value="docs" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs" data-testid="tab-docs">
            <FileText className="w-3 h-3 mr-1" /> Devis & Factures ({invoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-5">
          <AIProfileCard evaluation={overview.ai_evaluation} onEvaluate={evaluate} loading={evaluating} compact />
          {/* Description / notes */}
          {(o.notes || o.title) && (
            <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
              <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Détails</div>
              <div className="text-sm text-white">{o.title}</div>
              {o.notes && <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap">{o.notes}</p>}
            </div>
          )}
          <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Ajouter une note rapide
            </div>
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Compte rendu de réunion, prochaine étape, blocage…"
              rows={3}
              className="bg-[#14101c] border-white/10 text-white text-sm"
              data-testid="quick-note-input"
            />
            <div className="flex justify-end mt-2">
              <Button
                onClick={addNote}
                disabled={!noteContent.trim() || savingNote}
                size="sm"
                className="bg-[#3b82f6] text-white hover:bg-[#2563eb] text-xs"
                data-testid="quick-note-submit"
              >
                {savingNote ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />} Enregistrer
              </Button>
            </div>
          </div>
          <div>
            <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-3">Activité récente</h3>
            <EntityActivityTimeline timeline={timeline.slice(0, 5)} />
          </div>
        </TabsContent>

        <TabsContent value="ai">
          <AIProfileCard evaluation={overview.ai_evaluation} onEvaluate={evaluate} loading={evaluating} />
        </TabsContent>

        <TabsContent value="timeline">
          <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4">
            <EntityActivityTimeline timeline={timeline} />
          </div>
        </TabsContent>

        <TabsContent value="calls">
          {!calls.length ? (
            <div className="text-center py-10 text-sm text-slate-500">
              Aucun appel enregistré pour cette opportunité.
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map((c) => (
                <div key={c.call_log_id || c.aircall_call_id} className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid="call-row">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-slate-300">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${c.direction === "inbound" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}
                      >
                        {c.direction === "inbound" ? "ENTRANT" : "SORTANT"}
                      </span>
                      <span>{c.duration ? `${c.duration}s` : "—"}</span>
                      {c.phone && <span className="text-slate-500">{c.phone}</span>}
                    </div>
                    <span className="text-[10px] text-slate-500">{c.created_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  {c.ai_summary ? (
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="call-ai-summary">
                      {c.ai_summary}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500 italic">Pas de résumé IA disponible.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="docs">
          {!invoices.length ? (
            <div className="text-center py-10 text-sm text-slate-500">Aucun devis ni facture lié.</div>
          ) : (
            <div className="space-y-2">
              {invoices.map((inv) => (
                <div key={inv.invoice_id} className="bg-[#0b0811] rounded-xl border border-white/5 p-3.5 flex items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${inv.type === "facture" ? "bg-blue-500/15 text-blue-300" : inv.type === "avoir" ? "bg-rose-500/15 text-rose-300" : "bg-amber-500/15 text-amber-300"}`}
                      >
                        {inv.type}
                      </span>
                      <span className="text-sm font-semibold text-white">{inv.reference}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {inv.status} · émis le {(inv.issue_date || inv.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-white">{Number(inv.total_ttc || 0).toFixed(2)} €</div>
                    {inv.paid_amount > 0 && (
                      <div className="text-[11px] text-emerald-400">payé {Number(inv.paid_amount).toFixed(2)} €</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
